// Turns a raw Hardware Check result from the outpost into operator-facing status.
//
// The backend only reads the ports (raw lines per serial-settings profile); the weighbridge
// parsers are browser JavaScript, so parsing happens here with the SAME compile step the
// weighing screens use (useWeighbridgeCapture / SerialMonitorModal). Each port's data is
// also tried with every OTHER group's parser, which is how swapped cables are detected.

export const compileParser = (code) => {
    try {
        return new Function('data', (code || '') + '\nreturn parse(data);')
    } catch (e) {
        return null
    }
}

// Runs a parser over captured lines. A reading counts when the parser returns a real
// number (0 included, so an empty weighbridge still shows as readable).
const readWith = (fn, lines) => {
    let hits = 0
    let last = null
    if (!fn) return { hits, last }
    for (const line of lines || []) {
        let r
        try { r = fn(line) } catch (e) { continue }
        if (typeof r === 'number' && isFinite(r) && r >= 0) { hits += 1; last = r }
    }
    return { hits, last }
}

const captureFor = (wb, profile) => ((wb && wb.captures) || []).find((c) => c.profile === profile)

const weighbridgeStatus = (wb, parsedOk) => {
    if (!wb || !wb.configured) return 'NOT_CONFIGURED'
    if (wb.portExists === false) return 'PORT_MISSING'
    if (!wb.receiving) return 'NO_DATA'
    return parsedOk ? 'RECEIVING' : 'UNREADABLE'
}

// Works out which group's weighbridge is really on which port. Only groups that cannot
// read their own port are moved, and only when exactly one other port reads with their
// parser and settings. 2-cycles become SWAP pairs; anything else is shown as a mapping.
const suggest = (groups) => {
    const wrong = groups.map((g, i) => i).filter((i) => !groups[i].parsedOk && groups[i].weighbridge.receiving)
    const mapping = []
    for (const j of wrong) {
        const candidates = wrong.filter((i) => i !== j && groups[i].fits[j])
        if (candidates.length !== 1) return null // ambiguous or no match
        mapping.push({ group: j, port: candidates[0] })
    }
    if (mapping.length < 2) return null
    const ports = new Set(mapping.map((m) => m.port))
    if (ports.size !== mapping.length) return null // two groups claim the same port
    const pairs = []
    for (const m of mapping) {
        const back = mapping.find((x) => x.group === m.port)
        if (back && back.port === m.group && m.group < m.port) {
            pairs.push([groups[m.group].groupId, groups[m.port].groupId])
        }
    }
    const moves = mapping.map((m) => ({ groupId: groups[m.group].groupId, name: groups[m.group].name, onPortOf: groups[m.port].name }))
    return pairs.length * 2 === mapping.length ? { type: 'SWAP', pairs, moves } : { type: 'MAPPING', pairs, moves }
}

// 'green' all good | 'amber' camera issue or data not readable | 'red' a weighbridge is
// missing / silent, or ports look swapped | 'unknown' not checked yet.
const overallLevel = (groups, suggestion) => {
    if (!groups) return 'unknown'
    if (suggestion) return 'red'
    let level = 'green'
    for (const g of groups) {
        if (['PORT_MISSING', 'NO_DATA'].includes(g.wbStatus)) return 'red'
        if (g.wbStatus === 'UNREADABLE') level = 'amber'
        const cam = g.camera && g.camera.status
        if (cam && cam !== 'ONLINE' && cam !== 'NOT_CONFIGURED') level = 'amber'
    }
    return level
}

export function analyzeCheck(result) {
    if (!result || !Array.isArray(result.groups)) return { groups: null, suggestion: null, level: 'unknown' }
    const raw = result.groups
    const parsers = raw.map((g) => compileParser(g.weighbridge && g.weighbridge.parserFunction))
    const groups = raw.map((g, i) => {
        const wb = g.weighbridge || {}
        const own = readWith(parsers[i], (captureFor(wb, wb.profile) || {}).lines)
        const fits = raw.map((o, j) => {
            const c = captureFor(wb, o.weighbridge && o.weighbridge.profile)
            return !!c && readWith(parsers[j], c.lines).hits > 0
        })
        const parsedOk = own.hits > 0
        return { ...g, weighbridge: wb, parsedOk, weight: own.last, fits, wbStatus: weighbridgeStatus(wb, parsedOk) }
    })
    const suggestion = suggest(groups)
    return { groups, suggestion, level: overallLevel(groups, suggestion) }
}

// Groups whose adapter identity can be recorded now: their own parser reads their current
// port, the adapter has a unique serial number, none is stored yet, and nothing looks swapped.
export function adaptersToConfirm(analysis) {
    if (!analysis.groups || analysis.suggestion) return []
    return analysis.groups
        .filter((g) => g.parsedOk && !g.weighbridge.storedSerialNumber && g.weighbridge.serialNumber && g.weighbridge.serialUnique)
        .map((g) => g.groupId)
}
